// Motor de recuperación de sesión a nivel global.
//
// Supabase puede devolver errores de token como "JWT issued at future"
// (típicamente por un desfase de hora en el dispositivo del usuario) o
// "JWT expired". En vez de dejar que esos errores rompan una pantalla, este
// módulo intenta renovar la sesión una sola vez (single-flight) y, si no lo
// logra, aplica un cierre limpio de sesión y expone un aviso visible.
//
// Importante: este archivo NO importa el cliente de Supabase para evitar una
// dependencia circular. El cliente se registra mediante `registerRecoveryHandler`.

export type AuthRecoveryStatus = 'idle' | 'recovering' | 'failed';

export interface AuthRecoveryState {
  status: AuthRecoveryStatus;
  message: string;
}

type Listener = (state: AuthRecoveryState) => void;

const IDLE_STATE: AuthRecoveryState = { status: 'idle', message: '' };

let state: AuthRecoveryState = IDLE_STATE;
const listeners = new Set<Listener>();

function emit(next: AuthRecoveryState): void {
  state = next;
  listeners.forEach((listener) => listener(state));
}

export function subscribeAuthRecovery(listener: Listener): () => void {
  listeners.add(listener);
  listener(state);
  return () => {
    listeners.delete(listener);
  };
}

export function getAuthRecoveryState(): AuthRecoveryState {
  return state;
}

// El manejador intenta renovar la sesión y devuelve true si lo logró.
type RecoveryHandler = () => Promise<boolean>;
let recoveryHandler: RecoveryHandler | null = null;

export function registerRecoveryHandler(handler: RecoveryHandler | null): void {
  recoveryHandler = handler;
}

let recoveryPromise: Promise<boolean> | null = null;

async function runRecovery(): Promise<boolean> {
  emit({
    status: 'recovering',
    message: 'Tu sesión necesita renovarse. Estamos reconectando…',
  });

  let recovered = false;
  try {
    recovered = recoveryHandler ? await recoveryHandler() : false;
  } catch {
    recovered = false;
  }

  if (recovered) {
    emit(IDLE_STATE);
  } else {
    emit({
      status: 'failed',
      message:
        'Cerramos tu sesión por seguridad porque el token quedó con una hora inválida. ' +
        'Revisa que la fecha y hora de tu dispositivo estén en automático y vuelve a iniciar sesión.',
    });
  }

  return recovered;
}

// Dispara la recuperación. Múltiples errores simultáneos comparten una única
// ejecución (single-flight) para no disparar varias renovaciones en paralelo.
export function reportAuthError(): Promise<boolean> {
  if (recoveryPromise) return recoveryPromise;
  const promise = runRecovery().finally(() => {
    recoveryPromise = null;
  });
  recoveryPromise = promise;
  return promise;
}

export function clearAuthRecovery(): void {
  recoveryPromise = null;
  if (state.status !== 'idle') {
    emit(IDLE_STATE);
  }
}